import React from 'react';
import Icon from './Icon';

/* ── Decorative hero artwork ──────────────────────────────────────────
   The project ships no image assets, so the hero illustrations are drawn
   as SVG from the shared icon set. Purely decorative (aria-hidden). */

const float = (delay = 0) => ({
  animation: `floatBadge 5s ${delay}s ease-in-out infinite`,
});

// White floating bubble with an icon inside.
const Bubble = ({ x, y, r = 24, icon, color, delay }) => (
  <g transform={`translate(${x} ${y})`}>
    <g style={float(delay)}>
      <circle r={r} fill="#fff" filter="url(#dsHeroShadow)" />
      <g transform={`translate(${-r * 0.45} ${-r * 0.45})`} style={{ color }}>
        <Icon name={icon} size={r * 0.9} strokeWidth={2.2} />
      </g>
    </g>
  </g>
);

const Backdrop = () => (
  <>
    <defs>
      <radialGradient id="dsHeroGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ddd6fe" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#ddd6fe" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="dsHeroBrand" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6366f1" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
      <linearGradient id="dsHeroLeaf" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6ee7b7" />
        <stop offset="100%" stopColor="#10b981" />
      </linearGradient>
      <filter id="dsHeroShadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#4f46e5" floodOpacity="0.12" />
      </filter>
    </defs>
    <circle cx="250" cy="110" r="150" fill="url(#dsHeroGlow)" />
    <circle cx="250" cy="112" r="92" fill="#fff" opacity="0.55" />
    {/* dotted texture */}
    {Array.from({ length: 16 }).map((_, i) => (
      <circle key={i} cx={52 + (i % 4) * 12} cy={150 + Math.floor(i / 4) * 12} r="1.6" fill="#c4b5fd" opacity="0.7" />
    ))}
  </>
);

const Leaf = ({ x, y, s = 1, rotate = 0, opacity = 1 }) => (
  <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${s})`} opacity={opacity}>
    <path d="M0 0 C 18 -34, 58 -38, 70 -36 C 66 -18, 46 14, 0 0 Z" fill="url(#dsHeroLeaf)" />
    <path d="M2 -1 C 24 -16, 44 -26, 66 -34" stroke="#ecfdf5" strokeWidth="1.6" fill="none" opacity="0.8" />
  </g>
);

const ART = {
  // Admin: analytics card + clinical bubbles
  admin: (
    <>
      <Backdrop />
      <g filter="url(#dsHeroShadow)">
        <rect x="158" y="42" width="184" height="128" rx="16" fill="#fff" />
      </g>
      <rect x="176" y="60" width="60" height="8" rx="4" fill="#e0e7ff" />
      <rect x="176" y="74" width="38" height="6" rx="3" fill="#ede9fe" />
      {[34, 52, 26, 64, 44, 76, 58].map((h, i) => (
        <rect key={i} x={182 + i * 20} y={154 - h} width="11" height={h} rx="4"
          fill={i === 5 ? 'url(#dsHeroBrand)' : '#c7d2fe'} />
      ))}
      <Bubble x={354} y={58} icon="stethoscope" color="#4f46e5" delay={0} />
      <Bubble x={140} y={150} r={20} icon="heart" color="#ec4899" delay={1.2} />
      <Leaf x={330} y={196} s={0.8} rotate={-28} opacity={0.9} />
    </>
  ),
  // Patient: care-focused composition
  patient: (
    <>
      <Backdrop />
      <g filter="url(#dsHeroShadow)">
        <circle cx="250" cy="112" r="64" fill="url(#dsHeroBrand)" />
      </g>
      <g transform="translate(222 84)" style={{ color: '#fff' }}>
        <Icon name="activity" size={56} strokeWidth={2.4} />
      </g>
      <Bubble x={148} y={62} icon="shield" color="#10b981" delay={0} />
      <Bubble x={356} y={70} icon="plus" color="#2563eb" delay={0.8} />
      <Bubble x={160} y={168} r={20} icon="heart" color="#ec4899" delay={1.6} />
      <Bubble x={348} y={170} r={20} icon="calendar" color="#f97316" delay={2.2} />
      <Leaf x={300} y={46} s={0.55} rotate={-12} opacity={0.85} />
    </>
  ),
  // Doctor discovery: profile card with stethoscope
  doctor: (
    <>
      <Backdrop />
      <g filter="url(#dsHeroShadow)">
        <rect x="176" y="36" width="148" height="150" rx="18" fill="#fff" />
      </g>
      <circle cx="250" cy="86" r="30" fill="#eef2ff" />
      <g transform="translate(232 68)" style={{ color: '#4f46e5' }}>
        <Icon name="user" size={36} strokeWidth={2} />
      </g>
      <rect x="208" y="128" width="84" height="8" rx="4" fill="#e0e7ff" />
      <rect x="222" y="144" width="56" height="6" rx="3" fill="#ede9fe" />
      <g transform="translate(222 158)">
        {[0, 1, 2, 3, 4].map(i => (
          <g key={i} transform={`translate(${i * 12} 0)`} style={{ color: '#f59e0b' }}>
            <Icon name="star" size={10} fill="#f59e0b" strokeWidth={1} />
          </g>
        ))}
      </g>
      <Bubble x={350} y={66} icon="stethoscope" color="#4f46e5" delay={0} />
      <Bubble x={150} y={140} r={22} icon="video" color="#0891b2" delay={1} />
      <Leaf x={322} y={196} s={0.7} rotate={-30} opacity={0.9} />
    </>
  ),
  // Services: wellness composition
  wellness: (
    <>
      <Backdrop />
      <Leaf x={206} y={150} s={1.25} rotate={-40} />
      <Leaf x={262} y={160} s={1.05} rotate={-95} opacity={0.85} />
      <g filter="url(#dsHeroShadow)">
        <rect x="216" y="98" width="72" height="84" rx="16" fill="#fff" />
      </g>
      <rect x="230" y="88" width="44" height="16" rx="5" fill="url(#dsHeroBrand)" />
      <g transform="translate(236 124)" style={{ color: '#7c3aed' }}>
        <Icon name="plus" size={32} strokeWidth={2.6} />
      </g>
      <g transform="translate(320 136) rotate(-30)" filter="url(#dsHeroShadow)">
        <rect x="-26" y="-11" width="52" height="22" rx="11" fill="#fff" />
        <path d="M0 -11 h15 a11 11 0 0 1 0 22 h-15 z" fill="#f97316" />
      </g>
      <Bubble x={152} y={70} icon="heart" color="#ec4899" delay={0.4} />
      <Bubble x={356} y={60} icon="shield" color="#10b981" delay={1.4} />
    </>
  ),
};

export const HeroArt = ({ variant = 'patient' }) => (
  <div className="ds-hero-art" aria-hidden="true">
    <svg viewBox="80 0 320 220" preserveAspectRatio="xMidYMid meet">
      {ART[variant] || ART.patient}
    </svg>
  </div>
);

/**
 * Shared page hero used by every redesigned screen so titles, padding and
 * artwork sit on the same grid.
 *
 *   badge    – small pill above the title (node)
 *   title    – heading (node)
 *   subtitle – supporting copy
 *   children – optional actions (search bar, buttons) under the copy
 *   art      – HeroArt variant: 'admin' | 'patient' | 'doctor' | 'wellness'
 *   size     – 'xl' for the large marketing heading (Health Services)
 */
const PageHero = ({ badge, title, subtitle, children, art, size, tall, icon }) => (
  <section className={`ds-hero${tall ? ' ds-hero--tall' : ''}`}>
    <div className="ds-hero-content">
      {badge}
      <h1 className={`ds-hero-title${size === 'xl' ? ' ds-hero-title--xl' : ''}`}>
        {icon}
        <span>{title}</span>
      </h1>
      {subtitle && <p className="ds-hero-sub">{subtitle}</p>}
      {children && <div className="ds-hero-actions">{children}</div>}
    </div>
    {art && <HeroArt variant={art} />}
  </section>
);

export default PageHero;
